/**
 * SUNTRONIX ONE — Legal Acceptance Gate
 *
 * Blocking full-screen overlay that intercepts customer users on first login
 * (or after a new agreement version is published) and forces them to accept
 * every current NDA / DPA / Portal Access Terms before they can see the app.
 *
 * The user cannot dismiss the overlay — no close button, ESC ignored,
 * background click ignored.  Scroll-to-bottom unlocks the accept checkbox,
 * and each item must be accepted individually with a name + optional title.
 *
 * Evidence captured server-side: IP, User-Agent, timestamp, evidence hash.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, ChevronDown, FileText, Loader2, ShieldCheck, LogOut } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CODE_META = {
  NDA:           { icon: ShieldCheck, tone: "text-indigo-700",  label: "Corporate — Mutual Confidentiality" },
  DPA:           { icon: FileText,    tone: "text-emerald-700", label: "Corporate — Data Protection" },
  PORTAL_ACCESS: { icon: CheckCircle2,tone: "text-slate-700",   label: "Individual — Portal Access Terms" },
};

export default function LegalAcceptanceGate() {
  const { user, refresh, logout } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/legal/pending");
      setPending(data?.items || []);
    } catch {
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPending(); }, [loadPending]);

  const currentAgreement = pending[activeIdx];

  const onAccepted = async () => {
    // reload pending; if empty, refresh user session so app unlocks
    const nextIdx = activeIdx + 1;
    if (nextIdx >= pending.length) {
      toast.success("Thank you — access granted.");
      await refresh();
    } else {
      setActiveIdx(nextIdx);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] grid place-items-center bg-slate-900/70 backdrop-blur-sm" data-testid="legal-gate-loading">
        <Loader2 className="h-6 w-6 text-white animate-spin" />
      </div>
    );
  }

  if (!pending.length) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4" data-testid="legal-gate">
      <div className="w-full max-w-3xl max-h-[92vh] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <header className="px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">SUNTRONIX ONE · Legal Acceptance</div>
              <h1 className="text-[15px] font-semibold text-slate-900 truncate" data-testid="legal-gate-title">
                {pending.length === 1 ? "One agreement requires your acceptance" : `${pending.length} agreements require your acceptance`}
              </h1>
            </div>
            <button
              type="button"
              onClick={logout}
              data-testid="legal-gate-logout"
              className="h-8 px-3 rounded-md border border-slate-200 text-[12px] text-slate-600 hover:bg-white flex items-center gap-1.5"
              title="Sign out without accepting"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
          {/* Progress dots */}
          {pending.length > 1 && (
            <div className="mt-3 flex items-center gap-1.5" data-testid="legal-gate-progress">
              {pending.map((p, i) => (
                <div
                  key={p.id}
                  className={
                    "h-1.5 flex-1 rounded-full transition-colors " +
                    (i < activeIdx ? "bg-emerald-500" : i === activeIdx ? "bg-slate-900" : "bg-slate-200")
                  }
                />
              ))}
            </div>
          )}
        </header>

        {currentAgreement && (
          <AgreementBody
            key={currentAgreement.id}
            agreement={currentAgreement}
            user={user}
            onAccepted={onAccepted}
          />
        )}
      </div>
    </div>
  );
}

function AgreementBody({ agreement, user, onAccepted }) {
  const [full, setFull] = useState(null);
  const [signerName, setSignerName] = useState(user?.name || user?.email?.split("@")[0] || "");
  const [signerTitle, setSignerTitle] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  const meta = CODE_META[agreement.code] || CODE_META.PORTAL_ACCESS;
  const Icon = meta.icon;

  // Load full body (pending returns full doc but body_html may be missing; refetch current for safety)
  useEffect(() => {
    if (agreement?.body_html) { setFull(agreement); return; }
    (async () => {
      try {
        const { data } = await api.get("/legal/current");
        const found = (data?.items || []).find((i) => i.id === agreement.id);
        setFull(found || agreement);
      } catch {
        setFull(agreement);
      }
    })();
  }, [agreement]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 24 && !scrolledToBottom) setScrolledToBottom(true);
  };

  // If the agreement body fits the viewport (no scroll bar), there's
  // nothing to scroll — auto-satisfy the "scroll to end" gate so short
  // agreements don't leave the accept checkbox permanently disabled.
  useEffect(() => {
    if (!full || scrolledToBottom) return;
    const check = () => {
      const el = scrollRef.current;
      if (!el) return;
      if (el.scrollHeight - el.clientHeight < 24) setScrolledToBottom(true);
    };
    // Two ticks so the rendered HTML has laid out.
    const t1 = requestAnimationFrame(() => requestAnimationFrame(check));
    window.addEventListener("resize", check);
    return () => { cancelAnimationFrame(t1); window.removeEventListener("resize", check); };
  }, [full, scrolledToBottom]);

  const canSubmit = accepted && scrolledToBottom && signerName.trim().length >= 2 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/legal/accept", {
        agreement_id: agreement.id,
        signer_name: signerName.trim(),
        signer_title: signerTitle.trim() || undefined,
      });
      await onAccepted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record acceptance");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Meta strip */}
      <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-3 shrink-0">
        <Icon className={"h-4 w-4 " + meta.tone} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{meta.label}</div>
          <div className="text-[14px] font-semibold text-slate-900 truncate" data-testid="legal-agreement-title">
            {agreement.title} <span className="text-slate-400 font-normal">· {agreement.version}</span>
          </div>
        </div>
        {agreement.why && (
          <div className="hidden md:block max-w-[240px] text-[11px] text-slate-500 leading-tight">
            {agreement.why}
          </div>
        )}
      </div>

      {/* Body */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto px-6 py-4 legal-body"
        data-testid="legal-agreement-body"
      >
        {agreement.summary && (
          <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700">
            <span className="font-medium text-slate-900">Summary: </span>{agreement.summary}
          </div>
        )}
        {full ? (
          <div
            className="prose prose-slate prose-sm max-w-none prose-headings:font-semibold prose-h2:text-[16px] prose-h3:text-[13px] prose-p:text-[13px] prose-p:leading-relaxed"
            dangerouslySetInnerHTML={{ __html: full.body_html || "" }}
          />
        ) : (
          <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        )}
        {!scrolledToBottom && (
          <div className="sticky bottom-0 left-0 right-0 mt-4 flex justify-center pointer-events-none" data-testid="legal-scroll-hint">
            <div className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-slate-900 text-white text-[11px] shadow-lg">
              <ChevronDown className="h-3.5 w-3.5 animate-bounce" />
              Scroll to the end to enable acceptance
            </div>
          </div>
        )}
      </div>

      {/* Footer — signer + accept */}
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 shrink-0 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Your full name</label>
            <input
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Full legal name"
              data-testid="legal-signer-name"
              className="w-full h-9 px-3 mt-1 rounded-md border border-slate-300 bg-white text-[13px] focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Title (optional)</label>
            <input
              type="text"
              value={signerTitle}
              onChange={(e) => setSignerTitle(e.target.value)}
              placeholder="e.g. Global IT Director"
              data-testid="legal-signer-title"
              className="w-full h-9 px-3 mt-1 rounded-md border border-slate-300 bg-white text-[13px] focus:outline-none focus:border-slate-900"
            />
          </div>
        </div>

        <label className={"flex items-start gap-2 cursor-pointer " + (scrolledToBottom ? "" : "opacity-60 cursor-not-allowed")}>
          <input
            type="checkbox"
            disabled={!scrolledToBottom}
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            data-testid="legal-accept-checkbox"
            className="mt-0.5 h-4 w-4 accent-slate-900"
          />
          <span className="text-[12px] text-slate-700 leading-relaxed">
            I have read and agree to the <strong>{agreement.title} ({agreement.version})</strong>.
            {agreement.scope === "CORPORATE" ? " I confirm I am authorised to accept on behalf of my organisation." : " My electronic acceptance is binding."}
          </span>
        </label>

        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] text-slate-500">
            Evidence recorded: IP, browser, timestamp, and cryptographic hash.
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="legal-accept-submit"
            className="h-9 px-4 rounded-md bg-slate-900 text-white text-[13px] font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-800 flex items-center gap-1.5"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Accept and continue
          </button>
        </div>
      </div>
    </>
  );
}
